import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import documentService from '@api/v1/documentService'
import DocumentCard from '@components/common/DocumentCard'
import useReadState from '@hooks/useReadState'
import { toast } from '@store/toastStore'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useKeyboardShortcuts from '@hooks/useKeyboardShortcuts'

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const Documents = () => {
  useDocumentTitle('Documents')
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [showUpload, setShowUpload] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [uploadFiles, setUploadFiles] = useState([])
  const fileInputRef = useRef(null)

  const shortcuts = useMemo(() => [
    { key: 'n', meta: true, handler: () => setShowUpload(true) },
  ], [])
  useKeyboardShortcuts(shortcuts)

  const documentsQuery = useQuery({
    queryKey: ['documents', searchTerm],
    queryFn: () => documentService.getDocuments({
      limit: 500,
      searchTerm: searchTerm.length >= 2 ? searchTerm : undefined,
    }),
  })

  /*
   * CC-53132 — this page had the full three-way collapse in eight lines.
   *
   * `data?.documents || []` and `data?.total || 0` turned "we have not read your
   * documents" into "you have no documents", and then the header said so with a
   * number: "0 documents". `isLoading` cannot tell them apart — it is
   * `isPending && isFetching`, so it is FALSE for a query that is pending but not
   * in flight. The way that happens in the wild is a PAUSED fetch: with
   * react-query's default `networkMode: 'online'`, a query does not fire while
   * `onlineManager` believes the browser is offline. `queryFn` is never called,
   * so there is no request in any log on either side, nothing throws and nothing
   * is logged — the page just renders "0 documents" and "No documents found",
   * indefinitely and confidently. The `error` case was worse still: it was never
   * destructured at all, so a read that failed outright rendered the same empty
   * state as a firm with an empty document library.
   *
   * `useReadState` rather than the bare `readState` because it carries the
   * permanent blocked-read diagnostic (CC-49899 rule: a fail-soft path you cannot
   * observe is indistinguishable from a fix, and a paused fetch leaves no trace
   * anywhere by construction).
   */
  const documentsState = useReadState('documents', documentsQuery)
  const documentsReady = documentsState === 'ready'
  const data = documentsQuery.data
  // Deliberately NOT defaulted to `[]` / `0`. `undefined` is the honest value for
  // a read that has not resolved, and every render site below is gated on one of
  // the two predicates just under here, so neither is dereferenced in a state
  // where it would lie.
  const documents = data?.documents
  const total = data?.total

  // The predicate that decides whether this page may DESCRIBE the library at all —
  // including describe it as empty. `Array.isArray` is the same test `DataGrid`
  // settled on: a 200 whose body is not a list is not a resolved read either, and
  // `resp.meta.total` is a second field that can go missing independently of the
  // list, so the header's count needs its own check rather than riding on the
  // list's. Neither falls back to a number — an invented 0 is the whole defect.
  const documentsReadable = documentsReady && Array.isArray(documents)
  const totalReadable = documentsReadable && typeof total === 'number'

  const categories = useMemo(() => {
    const cats = new Set()
    // A local coercion for a DERIVATION, not for a rendered claim: on an
    // unresolved read this yields an empty category list that nothing displays,
    // because the subtitle that would quote its length only renders when the read
    // is ready.
    for (const doc of documents ?? []) {
      if (doc.category) cats.add(doc.category)
    }
    return Array.from(cats).sort()
  }, [documents])

  const uploadMutation = useMutation({
    mutationFn: (file) => documentService.uploadDocument(file),
    onSuccess: (_data, file) => {
      setUploadFiles((prev) =>
        prev.map((f) =>
          f.file === file ? { ...f, status: 'done' } : f
        )
      )
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      toast.success(`Uploaded ${file.name}`)
    },
    onError: (err, file) => {
      setUploadFiles((prev) =>
        prev.map((f) =>
          f.file === file
            ? { ...f, status: 'error', error: err.message }
            : f
        )
      )
      toast.error(`Failed to upload ${file.name}`)
    },
  })

  const addFiles = useCallback((fileList) => {
    const newFiles = Array.from(fileList).map((file) => ({
      file,
      name: file.name,
      size: file.size,
      status: 'pending',
    }))
    setUploadFiles((prev) => [...prev, ...newFiles])
    for (const { file } of newFiles) {
      uploadMutation.mutate(file)
    }
  }, [uploadMutation])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragActive(false)
    if (e.dataTransfer.files?.length) {
      addFiles(e.dataTransfer.files)
    }
  }, [addFiles])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setDragActive(false)
  }, [])

  const handleFileSelect = useCallback((e) => {
    if (e.target.files?.length) {
      addFiles(e.target.files)
      e.target.value = ''
    }
  }, [addFiles])

  const clearCompleted = () => {
    setUploadFiles((prev) => prev.filter((f) => f.status === 'pending'))
  }

  const handleCloseUpload = () => {
    setShowUpload(false)
    setUploadFiles([])
    setDragActive(false)
  }

  const allDone = uploadFiles.length > 0
    && uploadFiles.every((f) => f.status !== 'pending')

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">Documents</h1>
          {/* CC-53132: the count renders only on a COMPLETED read. A `0` beside an
              unfinished read is a claim, not an observation — and this one was
              phrased as a plain statement of fact ("0 documents"), sitting above a
              list area that agreed with it. Nothing stands in for it while the read
              is unresolved: the list area below carries the whole explanation, and
              a placeholder here would just be a second thing to read. */}
          {totalReadable && (
            <p className="text-sm text-gray-500 mt-1">
              {total} document{total !== 1 ? 's' : ''}
              {categories.length > 0 && ` across ${categories.length} categories`}
            </p>
          )}
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="px-4 py-2.5 bg-cb-accent text-white rounded-lg text-sm
            font-medium hover:bg-cb-accent/90 transition-colors shadow-sm"
        >
          + Upload Document
        </button>
      </div>

      {/* Upload Panel */}
      {showUpload && (
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100
          overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-gray-100 flex items-center
            justify-between"
          >
            <h2 className="text-sm font-semibold text-gray-900">
              Upload Documents
            </h2>
            <button
              onClick={handleCloseUpload}
              className="text-sm text-gray-500 hover:text-gray-600
                transition-colors"
            >
              Close
            </button>
          </div>

          {/* Drop zone */}
          <div
            className={`m-5 border-2 border-dashed rounded-xl p-8
              text-center transition-colors cursor-pointer ${
                dragActive
                  ? 'border-cb-accent bg-cb-accent/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <svg
              className={`w-10 h-10 mx-auto mb-3 ${
                dragActive ? 'text-cb-accent-text' : 'text-gray-300'
              }`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round" strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25
                  2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5
                  4.5M12 3v13.5"
              />
            </svg>
            <p className="text-sm text-gray-600 mb-1">
              {dragActive
                ? 'Drop files here'
                : 'Drag & drop files here, or click to browse'}
            </p>
            <p className="text-xs text-gray-500">
              Max 25 MB per file
            </p>
          </div>

          {/* Upload progress list */}
          {uploadFiles.length > 0 && (
            <div className="px-5 pb-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-500">
                  {uploadFiles.filter((f) => f.status === 'done').length}
                  {' / '}{uploadFiles.length} uploaded
                </p>
                {allDone && (
                  <button
                    onClick={clearCompleted}
                    className="text-xs text-gray-500 hover:text-gray-600
                      transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {uploadFiles.map((item, i) => (
                  <div
                    key={`${item.name}-${i}`}
                    className="flex items-center gap-3 px-3 py-2 bg-gray-50
                      rounded-lg"
                  >
                    {/* Status icon */}
                    {item.status === 'pending' && (
                      <div className="animate-spin w-4 h-4 border-2
                        border-cb-accent border-t-transparent rounded-full
                        shrink-0"
                      />
                    )}
                    {item.status === 'done' && (
                      <svg
                        className="w-4 h-4 text-cb-green shrink-0"
                        fill="none" viewBox="0 0 24 24"
                        stroke="currentColor" strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round" strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                    )}
                    {item.status === 'error' && (
                      <svg
                        className="w-4 h-4 text-red-500 shrink-0"
                        fill="none" viewBox="0 0 24 24"
                        stroke="currentColor" strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round" strokeLinejoin="round"
                          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0
                            0118 0zm-9 3.75h.008v.008H12v-.008z"
                        />
                      </svg>
                    )}

                    {/* File info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">
                        {item.name}
                      </p>
                      {item.status === 'error' && (
                        <p className="text-xs text-red-500 truncate">
                          {item.error || 'Upload failed'}
                        </p>
                      )}
                    </div>

                    {/* Size */}
                    <span className="text-xs text-gray-500 shrink-0">
                      {formatFileSize(item.size)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 max-w-md relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4
              text-gray-500"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round" strokeLinejoin="round"
              d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196
                5.196a7.5 7.5 0 0010.607 10.607z"
            />
          </svg>
          {/* aria-label, not placeholder-only -- see CC-53430. */}
          <input
            type="text"
            placeholder="Search documents..."
            aria-label="Search documents"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border
              border-gray-200 rounded-lg text-sm text-gray-900
              placeholder:text-gray-500 focus:outline-none focus:ring-2
              focus:ring-cb-accent/30 focus:border-cb-accent"
          />
        </div>
      </div>

      {/* Document List

          CC-53132: this was a two-way `isLoading ? spinner : empty-or-list`, which
          means three of the five read states rendered "No documents found". The
          chain below names all five. `idle` rides with `loading` deliberately:
          this query has no `enabled` option, so it cannot reach that state today —
          but if it is ever gated (a matter-scoped document list is the obvious
          next step) an unhandled fifth state falls through every branch here and
          renders a BLANK PAGE, which is exactly the trap the first version of the
          catalog fix left behind. Folding it into loading is safe because the
          query is unconditional; the day it gets an `enabled` gate, revisit this
          comment rather than the spinner. */}
      {documentsState === 'loading' || documentsState === 'idle' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100
          p-12 text-center"
        >
          <div className="animate-spin w-6 h-6 border-2 border-cb-accent
            border-t-transparent rounded-full mx-auto mb-3"
          />
          <p className="text-sm text-gray-500">Loading documents...</p>
        </div>
      ) : documentsState === 'blocked' ? (
        /* Paused: react-query never called `queryFn`, so nothing on this screen is
           known. Says in as many words that this is not an empty library, because
           "No documents found" is what the user used to see here and they have no
           other way to tell the difference. A spinner forever would be almost as
           bad — it at least does not assert anything, but it also never ends. */
        <div
          role="alert"
          className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm
            text-amber-900"
        >
          <p className="font-medium">
            Can’t reach the server, so we haven’t loaded your documents.
          </p>
          <p className="mt-1 text-amber-800">
            This is not an empty document library — the request was never sent.
            Your connection looks offline; check it and try again.
          </p>
          <button
            type="button"
            onClick={() => documentsQuery.refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3
              py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      ) : (documentsState === 'failed' || !documentsReadable) ? (
        /* The read threw. Before this it had no render site at all on this page —
           `error` was never even destructured — so a 500 from `/documents` looked
           identical to a firm that has uploaded nothing. Try again refetches
           rather than making the user reload the app.

           The `!documentsReadable` half rides here because by this point the state
           can only be 'ready' or 'failed', so the extra thing it catches is a
           request that SUCCEEDED with a body that was not a list — the third shape
           `readShape.js` exists for. The old `|| []` swallowed that into "No
           documents found"; with the default gone it would instead throw on
           `.length` mid-render. It also guarantees the two branches below are
           looking at a real array. */
        <div
          role="alert"
          className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm
            text-red-700"
        >
          <p className="font-medium">
            {documentsQuery.error
              ? `Failed to load documents: ${documentsQuery.error.message}`
              : 'The document list came back in a shape we couldn’t read.'}
          </p>
          <p className="mt-1">
            This is not an empty document library — we have no readable list of
            your files, so nothing on this page is a count of them.
          </p>
          <button
            type="button"
            onClick={() => documentsQuery.refetch()}
            className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5
              text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      ) : documents.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100
          p-12 text-center"
        >
          <svg
            className="w-12 h-12 text-gray-200 mx-auto mb-3"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
            strokeWidth={1}
          >
            <path
              strokeLinecap="round" strokeLinejoin="round"
              d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125
                1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25
                0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125
                1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0
                00-9-9z"
            />
          </svg>
          <p className="text-sm text-gray-500">No documents found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <DocumentCard key={doc.id} doc={doc} />
          ))}
        </div>
      )}
    </div>
  )
}

export default Documents
